import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useCanDeleteAutomation, useCanRunAutomation, type PlainWorkflow } from "@/queries/automation-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";
import { DeleteWorkflowDialog } from "@/components/automations/DeleteAutomationDialog";
import { RunAutomationDialog } from "@/components/automations/RunAutomationDialog";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { Button, type ButtonSize } from "@/components/flexkit/Button";
import { isWorkflowDeleting } from "@/utils/automations";

export const WorkflowActionsDropdown: FC<{
    workflow: PlainWorkflow;
    showRunAction: boolean;
    buttonSize: ButtonSize;
    onOpenChange?: (open: boolean) => void;
}> = ({ workflow, showRunAction, buttonSize, onOpenChange }) => {
    const { toast } = useToast();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
    const [showRunDialog, setShowRunDialog] = useState(false);
    const isDeleting = isWorkflowDeleting(workflow);
    const { canDeleteAutomation } = useCanDeleteAutomation(workflow.id);
    const { canRunAutomation } = useCanRunAutomation(workflow.id);

    const handleRunWorkflow = useCallback(() => {
        setShowRunDialog(true);
    }, []);

    const handleCopyId = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(workflow.id);
            toast({
                title: "Automation ID copied to clipboard",
                description: workflow.id,
            });
        } catch (error) {
            toast({
                title: "Failed to copy automation ID",
                description: formatError(error),
            });
        }
    }, [toast, workflow.id]);

    const handleDelete = useCallback(() => setShowDeleteModal(true), []);
    const handleForceDelete = useCallback(() => setShowForceDeleteModal(true), []);

    return (
        <div
            className="flex h-9 items-center"
            onClick={(e) => {
                e.stopPropagation();
                e.stopPropagation();
            }}
            data-tracking-id-none
        >
            <DropdownActions
                triggerTestId="workflow-actions-dropdown-trigger"
                button={
                    <Button
                        variant="outline"
                        aria-label="More actions"
                        LeadingIcon={IconEllipsisHorizontal}
                        size={buttonSize}
                    />
                }
                onOpenChange={onOpenChange}
                contentClassName="w-48"
            >
                <DropdownMenuItem
                    onClick={handleCopyId}
                    data-testid="workflow-actions-dropdown-copy-id"
                    data-tracking-id="copy-id-workflow-actions-dropdown"
                >
                    Copy Automation ID
                </DropdownMenuItem>
                {showRunAction && canRunAutomation && (
                    <DropdownMenuItem
                        onClick={handleRunWorkflow}
                        disabled={isDeleting}
                        data-testid="workflow-card-actions-run-workflow"
                        data-tracking-id="run-workflow-workflow-card-actions"
                    >
                        Run
                    </DropdownMenuItem>
                )}
                {canDeleteAutomation &&
                    (isDeleting ? (
                        <DropdownMenuItem
                            variant="destructive"
                            onClick={handleForceDelete}
                            data-testid="workflow-actions-dropdown-force-delete"
                            data-tracking-id="force-delete-workflow-actions-dropdown"
                        >
                            Force Delete
                        </DropdownMenuItem>
                    ) : (
                        <DropdownMenuItem
                            variant="destructive"
                            onClick={handleDelete}
                            data-testid="workflow-actions-dropdown-delete"
                            data-tracking-id="delete-workflow-actions-dropdown"
                        >
                            Delete
                        </DropdownMenuItem>
                    ))}
            </DropdownActions>
            <DeleteWorkflowDialog
                open={showDeleteModal}
                workflow={workflow}
                onClose={() => setShowDeleteModal(false)}
            />
            <DeleteWorkflowDialog
                open={showForceDeleteModal}
                workflow={workflow}
                onClose={() => setShowForceDeleteModal(false)}
                forceDelete={true}
            />
            <RunAutomationDialog open={showRunDialog} onOpenChange={setShowRunDialog} workflow={workflow} />
        </div>
    );
};
